import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { MapContainer, ImageOverlay, Popup, Polygon, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapLayout } from 'components/mapchart/MapLayout';
import { StatusBadge, StatusItem } from 'components/home/StatusBadge';
import { CommonText } from 'ui/theme/Color';
import { NormalButton } from 'components/buttons/ButtonStyle';
import CustomDialog from 'components/modals/common/CustomDialog';
import ImageSlider from './ImageSlide';
import { SetupMapChart } from 'models/setup/SetupMapChart';
import { API_BASE_URL } from 'utils/ApiUtil';
import { useTranslation } from 'react-i18next';

export interface PolygonData {
  tgtEqpId?: string;
  eqpId?: string;
  eqpNm?: string;
  excpId?: string;
  minXcrd?: number | string;
  maxXcrd?: number | string;
  minYcrd?: number | string;
  maxYcrd?: number | string;
  atchFileGrId?: string;
  atchFileIdList?: string;
  code?: string;
  selected?: boolean;
}

export interface MapChartProps {
  className?: string;
  polygonData: PolygonData[];
  setPolygonData: Dispatch<SetStateAction<SetupMapChart[]>>;
  imageId: string;
  imageWidth?: number;
  imageHeight?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  showPopup?: boolean;
  config?: MapChartConfig;
  onClickPolygon?: (data: any, e: L.LeafletMouseEvent) => void;
  onClickBadge?: (data: any, e: L.LeafletMouseEvent) => void;
  badgeList?: BadgeItem[];
  badgeKey?: string;
  onSiteImgList?: SiteImage[];
}
export interface MapChartConfig {
  minZoom?: number;
  maxZoom?: number;
  zoomSnap?: number;
  zoomDelta?: number;
  doubleClickZoom?: boolean;
}
export interface BadgeItem {
  code: string;
  color: string;
  text?: string;
  selected?: boolean;
}
export interface SiteImage {
  id?: string;
  sid?: string;
  dataInsDtm?: string;
}

const MapChart = ({
  className = '',
  polygonData = [],
  setPolygonData,
  imageId = '',
  imageWidth = 0,
  imageHeight = 0,
  showLegend = true,
  showTooltip = false,
  showPopup = false,
  config = {},
  onClickPolygon,
  onClickBadge,
  badgeList = [],
  badgeKey = 'no-key',
  onSiteImgList = [],
}: MapChartProps) => {
  const { t } = useTranslation();
  // Default config setting
  const CONFIG = {
    minZoom: -4,
    maxZoom: 0,
    zoomSnap: 0.2,
    zoomDelta: 0.2,
    doubleClickZoom: false,
    ...config,
  };
  const [imageBounds, setImageBounds] = useState<[[number, number], [number, number]]>([
    [-imageHeight, 0],
    [0, imageWidth],
  ]);
  const [showDetailImg, setShowDetailImg] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PolygonData>();

  const badgeStateList = useMemo(() => {
    const list = badgeList.map((o) => {
      const filter = polygonData.filter((x) => x[badgeKey] === o.code);
      return { ...o, selected: filter.length > 0 && filter.every((x) => x.selected) };
    });
    return list;
  }, [polygonData]);

  // string to number 변환 함수
  const parseNumber = (v: number | string | undefined, fallback = 0): number => {
    if (v === undefined || v === null) return fallback;
    if (typeof v === 'number') return isNaN(v) ? fallback : v;
    // remove commas and whitespace
    const sanitized = String(v).replace(/,/g, '').trim();
    const n = Number(sanitized);
    return Number.isFinite(n) ? n : fallback;
  };

  const onClickDefaultPolygon = (poly: PolygonData, e: L.LeafletMouseEvent) => {
    // stop propagation so map-level click handler doesn't fire for polygon clicks
    try {
      e.originalEvent && e.originalEvent.stopPropagation && e.originalEvent.stopPropagation();
    } catch (err) {
      // ignore
    }
    setPolygonData((prev) => prev.map((o) => ({ ...o, selected: o.tgtEqpId === poly.tgtEqpId })));
    if (onClickPolygon) onClickPolygon(poly, e);
  };

  const onClickDefaultBadge = (data: BadgeItem, e: any) => {
    setPolygonData((prev) =>
      prev.map((o) => {
        if (o[badgeKey] === data.code) o.selected = !data.selected;
        return o;
      })
    );
    if (onClickBadge) onClickBadge(data, e);
  };

  // 이미지 파일이 없는 경우 표시할 UI
  if (!imageId) {
    return <MapLayout className={className}>No Image</MapLayout>;
  }

  // 이미지 가로/세로 데이터 가져오기
  useEffect(() => {
    if (!imageId) return;

    if (imageWidth > 0 && imageHeight > 0) {
      setImageBounds([
        [-imageHeight, 0],
        [0, imageWidth],
      ]);
    } else {
      const img = new Image();
      img.src = `${API_BASE_URL}/v1/file/drw-image?id=${imageId}`;

      img.onload = () => {
        const bounds: [[number, number], [number, number]] = [
          [-img.height, 0],
          [0, img.width],
        ];
        setImageBounds(bounds);
      };

      img.onerror = () => {
        setImageBounds([
          [0, 0],
          [0, 0],
        ]);
      };

      // clean up
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [imageId, imageWidth, imageHeight]);

  return (
    <MapLayout className={className}>
      <MapContainer
        key={`${imageId}-${imageBounds}`}
        crs={L.CRS.Simple}
        bounds={imageBounds}
        attributionControl={false}
        {...CONFIG}
        style={{
          height: '100%',
          width: '100%',
          backgroundColor: 'white',
        }}
      >
        <ImageOverlay
          bounds={imageBounds}
          url={`${API_BASE_URL}/v1/file/drw-image?id=${imageId}`}
        />
        <ResetControl bounds={imageBounds} setPolygonData={setPolygonData} />
        <MapOpserver bounds={imageBounds} />
        <MapClickHandler setPolygonData={setPolygonData} />
        {polygonData.map((item, idx) => {
          const poly = {
            ...item,
            minXcrd: parseNumber(item.minXcrd, 0),
            maxXcrd: parseNumber(item.maxXcrd, 0),
            minYcrd: parseNumber(item.minYcrd, 0),
            maxYcrd: parseNumber(item.maxYcrd, 0),
          };
          const positions: L.LatLngTuple[] = [
            [poly.minYcrd, poly.minXcrd],
            [poly.maxYcrd, poly.minXcrd],
            [poly.maxYcrd, poly.maxXcrd],
            [poly.minYcrd, poly.maxXcrd],
          ];
          const color = badgeList.find((o) => o.code === poly[badgeKey])?.color || CommonText.Light;

          return (
            <Polygon
              key={`polygon_${idx}`}
              positions={positions}
              className={`transition-all duration-500`}
              pathOptions={{
                weight: 1,
                color: !poly.selected ? color : 'black', // 테두리 색
                fillColor: !poly.selected ? 'white' : color,
                fillOpacity: 1,
                opacity: 0.5,
              }}
              eventHandlers={{
                click: (e) => onClickDefaultPolygon(poly, e),
              }}
            >
              {showPopup && (
                <Popup>
                  <div style={{ width: 260 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                      }}
                    >
                      <strong>{poly.eqpNm}</strong>
                    </div>
                    {onSiteImgList.length > 0 && onSiteImgList[0] && (
                      <>
                        <div
                          style={{
                            width: '100%',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 8,
                          }}
                        >
                          <img
                            src={`${API_BASE_URL}/v1/file/drw-image?id=${onSiteImgList[0].id}&sid=${onSiteImgList[0].sid}`}
                            alt={poly.eqpNm}
                            style={{
                              width: '100%',
                              aspectRatio: 16 / 9,
                              maxWidth: '150px',
                              maxHeight: '150px',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <NormalButton
                            className="noIconBtn minWidth"
                            onClick={() => {
                              setShowDetailImg(true);
                              setSelectedItem({ ...poly });
                            }}
                          >
                            {t('setup.label.더보기', '더보기')}
                          </NormalButton>
                        </div>
                      </>
                    )}
                  </div>
                </Popup>
              )}
              {showTooltip && (
                <Tooltip>
                  <div className="polygon-tooltip">
                    <h4>{poly.eqpNm}</h4>
                  </div>
                </Tooltip>
              )}
            </Polygon>
          );
        })}
      </MapContainer>
      {showLegend && (
        <StatusBadge className="statusBadge">
          {badgeStateList?.length > 0 &&
            badgeStateList.map((item, idx) => {
              return (
                <StatusItem
                  key={idx}
                  background={item.color}
                  opacity={item.selected ? '1' : '0.4'}
                  onClick={(e) => onClickDefaultBadge(item, e)}
                >
                  {item.text || item.code}
                </StatusItem>
              );
            })}
        </StatusBadge>
      )}

      <CustomDialog
        title={selectedItem?.eqpNm || ''}
        open={showDetailImg}
        onClose={() => setShowDetailImg(false)}
      >
        <ImageSlider
          images={onSiteImgList.map((o) => ({
            img: `${API_BASE_URL}/v1/file/drw-image?id=${o.id}&sid=${o.sid}`,
            date: o.dataInsDtm,
          }))}
          alt={`${selectedItem?.eqpNm} Image`}
        />
      </CustomDialog>
    </MapLayout>
  );
};

// Zoom Reset 버튼
const ResetControl: FC<{
  bounds: [[number, number], [number, number]];
  setPolygonData: Dispatch<SetStateAction<SetupMapChart[]>>;
}> = ({ bounds, setPolygonData }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;

    const Reset = L.Control.extend({
      onAdd: function () {
        const container = L.DomUtil.create('div', 'leaflet-control-reset leaflet-bar');
        container.style.marginLeft = '10px';
        container.style.marginTop = '0';

        //  css={IconButton.button} className="reload"
        const btn = L.DomUtil.create('a', 'leaflet-control-reset-button', container);
        btn.href = '#';
        btn.style.padding = '6px 8px';
        btn.style.display = 'block';
        btn.style.textDecoration = 'none';
        btn.style.color = '#111827';
        btn.style.borderRadius = '4px';
        btn.style.backgroundImage = `url("data:image/svg+xml,%3Csvg width='100%' height='100%' viewBox='0 0 16 16' fill='' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.30229 3.71754C4.20665 2.71315 5.40577 2.02084 6.72778 1.73984C8.04979 1.45883 9.42683 1.60357 10.6615 2.15329C11.8962 2.70301 12.9252 3.62951 13.601 4.79998C13.7114 4.9913 13.6459 5.23593 13.4546 5.34639C13.2633 5.45685 13.0186 5.3913 12.9082 5.19998C12.3169 4.17582 11.4165 3.36513 10.3361 2.88413C9.25578 2.40312 8.05087 2.27648 6.89411 2.52235C5.73735 2.76823 4.68812 3.374 3.8968 4.25285C3.10549 5.13169 2.61271 6.23849 2.48909 7.41462C2.466 7.63432 2.26918 7.79371 2.04947 7.77062C1.82977 7.74753 1.67038 7.5507 1.69347 7.331C1.83475 5.98685 2.39793 4.72194 3.30229 3.71754ZM2.66237 10.6535C2.85369 10.543 3.09833 10.6086 3.20878 10.7999C3.80009 11.8241 4.70044 12.6348 5.7808 13.1158C6.86116 13.5968 8.06607 13.7234 9.22283 13.4775C10.3796 13.2317 11.4288 12.6259 12.2201 11.747C13.0115 10.8682 13.5042 9.7614 13.6278 8.58527C13.6509 8.36557 13.8478 8.20618 14.0675 8.22928C14.2872 8.25237 14.4466 8.44919 14.4235 8.6689C14.2822 10.013 13.719 11.278 12.8147 12.2824C11.9103 13.2867 10.7112 13.9791 9.38916 14.2601C8.06715 14.5411 6.69011 14.3963 5.45541 13.8466C4.22071 13.2969 3.19174 12.3704 2.51596 11.1999C2.40551 11.0086 2.47106 10.764 2.66237 10.6535Z' fill='%235B5C5B'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.6617 5.34203C10.6963 5.12383 10.9012 4.97497 11.1194 5.00953L13.2545 5.3477C13.4 5.37074 13.5366 5.27149 13.5596 5.12603L13.8978 2.99089C13.9324 2.77269 14.1373 2.62383 14.3555 2.65838C14.5737 2.69294 14.7225 2.89784 14.688 3.11603L14.3498 5.25118C14.2576 5.83303 13.7112 6.23001 13.1294 6.13785L10.9942 5.79968C10.7761 5.76512 10.6272 5.56022 10.6617 5.34203Z' fill='%235B5C5B'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5.58395 11.0753C5.52677 11.2887 5.30743 11.4153 5.09405 11.3581L3.00595 10.7986C2.86369 10.7605 2.71747 10.8449 2.67935 10.9872L2.11984 13.0753C2.06267 13.2887 1.84333 13.4153 1.62995 13.3581C1.41656 13.301 1.28993 13.0816 1.3471 12.8682L1.90661 10.7801C2.05908 10.2111 2.64397 9.87342 3.213 10.0259L5.3011 10.5854C5.51449 10.6426 5.64112 10.8619 5.58395 11.0753Z' fill=''/%3E%3C/svg%3E%0A")`;
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.on(btn, 'click', (ev: Event) => {
          L.DomEvent.stop(ev);
          // Zoom & Position Reset
          try {
            if (!bounds) return;
            map.fitBounds(bounds);
          } catch (err) {
            const [[y1, x1], [y2, x2]] = bounds;
            const cx = (x1 + x2) / 2;
            const cy = (y1 + y2) / 2;
            map.setView([cy, cx], map.getZoom());
          }

          // Selected Reset
          setPolygonData((prev) => prev.map((o) => ({ ...o, selected: true })));

          // Popup Reset
          map.closePopup();
        });

        return container;
      },
    });

    const ctrl = new Reset({ position: 'topleft' });
    map.addControl(ctrl);
    return () => {
      try {
        map.removeControl(ctrl);
      } catch (e) {
        /* ignore */
      }
    };
  }, [map, bounds]);

  return null;
};

// Map click handler
const MapClickHandler: FC<{
  setPolygonData: Dispatch<SetStateAction<SetupMapChart[]>>;
}> = ({ setPolygonData }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const handler = (e: any) => {
      setPolygonData((prev) => prev.map((o) => ({ ...o, selected: true })));
    };
    map.on('click', handler);
    map.closePopup();
    return () => {
      map.off('click', handler);
    };
  }, [map]);
  return null;
};

// mapChart observe
const MapOpserver: FC<{ bounds: [[number, number], [number, number]] }> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (!container) return;

    const observer = new ResizeObserver(() => {
      const { width, height } = container.getBoundingClientRect();
      if (width > 0 && height > 0) {
        requestAnimationFrame(() => {
          try {
            map.invalidateSize();
            if (bounds) {
              map.fitBounds(bounds, { animate: false });
            }
          } catch {
            // invalidateSize skipped
          }
        });
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [map, bounds]);

  return null;
};

export default MapChart;
